/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

import { useSelector, useDispatch } from "react-redux";

import { getWheelPoint, getPlayerCash } from "../store/slices/user";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CheckinExperience = ({ currentUser, showToast = () => {} }) => {
  const dispatch = useDispatch();
  const { singleUsers } = useSelector((state) => state.user || {});
  const user = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const [checkinActivity, setCheckinActivity] = useState(null);
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinError, setCheckinError] = useState(null);
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchCheckinActivity = useCallback(
    async (options = {}) => {
      if (!API_BASE_URL) return null;
      const { signal, silent } = options;
      if (!silent && isMountedRef.current) {
        setCheckinLoading(true);
      }
      if (isMountedRef.current) {
        setCheckinError(null);
      }
      try {
        const query = currentUser?.id ? `?userId=${currentUser.id}` : "";
        const response = await fetch(`${API_BASE_URL}/checkin/active${query}`, {
          signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.message || "ไม่สามารถโหลดกิจกรรมเช็คอินได้");
        }
        if (!signal?.aborted && isMountedRef.current) {
          setCheckinActivity(payload?.data || null);
        }
        return payload?.data || null;
      } catch (error) {
        if (error.name === "AbortError" || signal?.aborted) {
          return null;
        }
        if (!signal?.aborted && isMountedRef.current) {
          setCheckinActivity(null);
          setCheckinError(error.message || "เกิดข้อผิดพลาด");
        }
        return null;
      } finally {
        if (!silent && isMountedRef.current) {
          setCheckinLoading(false);
        }
      }
    },
    [currentUser?.id],
  );

  useEffect(() => {
    if (!API_BASE_URL) return;
    const controller = new AbortController();
    fetchCheckinActivity({ signal: controller.signal });
    return () => {
      controller.abort();
    };
  }, [fetchCheckinActivity]);

  const buildCheckinImageUrl = (path) => {
    if (!path) {
      return "https://placehold.co/80x80?text=Check-in";
    }
    if (/^https?:/i.test(path)) {
      return path;
    }
    const base = (API_BASE_URL || "").replace(/\/$/, "");
    return `${base}${path}`;
  };

  const sortedCheckinRewards = useMemo(() => {
    if (!checkinActivity?.rewards) return [];
    return [...checkinActivity.rewards].sort(
      (a, b) => (a.dayNumber || 0) - (b.dayNumber || 0),
    );
  }, [checkinActivity]);

  const totalCheckinRewards = useMemo(() => {
    if (!sortedCheckinRewards.length) return { cash: 0, diamond: 0 };
    return sortedCheckinRewards.reduce(
      (acc, reward) => {
        const amount = Number(reward.rewardAmount) || 0;
        if (reward.rewardType === "cashWallet") {
          acc.cash += amount;
        } else {
          acc.diamond += amount;
        }
        return acc;
      },
      { cash: 0, diamond: 0 },
    );
  }, [sortedCheckinRewards]);

  const progressState = useMemo(() => {
    if (!checkinActivity) return {};
    const base = checkinActivity.progress || checkinActivity.userProgress || {};
    const normalizedClaimed = Array.isArray(base.claimedDays)
      ? base.claimedDays.map((day) => Number(day) || 0).filter((day) => day > 0)
      : [];
    return {
      ...base,
      currentDay: Number(base.currentDay) || 0,
      streakCount: Number(base.streakCount) || 0,
      claimedDays: normalizedClaimed,
      completed: Boolean(base.completed),
      lastCheckinDate: base.lastCheckinDate || null,
    };
  }, [checkinActivity]);

  const claimedDayList = useMemo(() => {
    return Array.isArray(progressState.claimedDays)
      ? progressState.claimedDays
      : [];
  }, [progressState.claimedDays]);

  const claimedDaysCount = useMemo(() => {
    if (claimedDayList.length) {
      return claimedDayList.length;
    }
    const value =
      progressState.completedDays ??
      progressState.currentDay ??
      progressState.checkedDays ??
      0;
    return Number(value) || 0;
  }, [
    claimedDayList,
    progressState.completedDays,
    progressState.currentDay,
    progressState.checkedDays,
  ]);

  const focusDayNumber = useMemo(() => {
    const totalDays =
      sortedCheckinRewards.length || checkinActivity?.totalDays || 1;
    if (!totalDays) return 1;
    if (progressState.completed) {
      return Math.min(progressState.currentDay || totalDays, totalDays);
    }
    const baseDay = Math.max(
      claimedDaysCount,
      progressState.currentDay || claimedDaysCount,
    );
    const nextDay = baseDay + 1;
    return Math.min(Math.max(nextDay, 1), totalDays);
  }, [
    claimedDaysCount,
    progressState.completed,
    progressState.currentDay,
    sortedCheckinRewards,
    checkinActivity,
  ]);

  const totalConfiguredDays =
    sortedCheckinRewards.length || checkinActivity?.totalDays || 0;

  const completedDaysDisplay = Math.min(
    progressState.currentDay || claimedDaysCount,
    totalConfiguredDays || 0,
  );

  const formatCheckinDate = useCallback(
    (dayNumber) => {
      if (!checkinActivity?.startDate) return null;
      const base = new Date(checkinActivity.startDate);
      if (Number.isNaN(base.getTime())) return null;
      const target = new Date(base);
      target.setDate(target.getDate() + (dayNumber - 1));
      const day = `${target.getDate()}`.padStart(2, "0");
      const month = `${target.getMonth() + 1}`.padStart(2, "0");
      const year = target.getFullYear();
      return `${day}-${month}-${year}`;
    },
    [checkinActivity?.startDate],
  );

  const getDayStatus = useCallback(
    (dayNumber) => {
      if (claimedDayList.includes(dayNumber)) return "completed";
      if (!progressState.completed && dayNumber === focusDayNumber)
        return "current";
      if (progressState.completed && dayNumber <= progressState.currentDay)
        return "completed";
      return "upcoming";
    },
    [
      claimedDayList,
      focusDayNumber,
      progressState.completed,
      progressState.currentDay,
    ],
  );

  const describeReward = useCallback((reward) => {
    const amount = Number(reward.rewardAmount || 0).toLocaleString();
    return reward.rewardType === "cashWallet"
      ? `${amount} ฿`
      : `${amount} เพชร`;
  }, []);

  const toDateKey = useCallback((value) => {
    if (!value) return null;
    if (typeof value === "string") {
      const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (matched) {
        return `${matched[1]}-${matched[2]}-${matched[3]}`;
      }
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const hasCheckedInToday = useMemo(() => {
    const lastCheckinDate = toDateKey(progressState.lastCheckinDate);
    const today = toDateKey(new Date());
    return Boolean(lastCheckinDate && today && lastCheckinDate === today);
  }, [progressState.lastCheckinDate, toDateKey]);

  const shouldRenderCheckinWidget = Boolean(checkinActivity);
  const checkinThemeColor =
    checkinActivity?.themeColor || "var(--primaryColor)";

  const handleCheckinButtonClick = () => {
    if (checkinSubmitting) return;
    if (!shouldRenderCheckinWidget) return;
    setCheckinModalOpen(true);
  };

  const closeCheckinModal = () => {
    if (!checkinSubmitting) {
      setCheckinModalOpen(false);
    }
  };

  const handleStartCheckin = async () => {
    if (!checkinActivity?.id) return;
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบเพื่อบันทึกเช็คอิน", "warning");
      return;
    }
    if (checkinSubmitting) return;
    if (hasCheckedInToday) {
      showToast("วันนี้คุณเช็คอินแล้ว", "warning");
      await fetchCheckinActivity({ silent: true });
      return;
    }

    if (isMountedRef.current) {
      setCheckinSubmitting(true);
    }
    try {
      const response = await fetch(`${API_BASE_URL}/checkin/active/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: currentUser.id,
          eventId: checkinActivity.id,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "ไม่สามารถบันทึกการเช็คอินได้");
      }
      if (isMountedRef.current) {
        showToast(payload?.message || "เช็คอินสำเร็จ", "info");
        dispatch(getWheelPoint(user));
        dispatch(getPlayerCash(user));
        setCheckinModalOpen(false);
      }
      await fetchCheckinActivity({ silent: true });
    } catch (error) {
      const errorMessage = error.message || "ระบบเช็คอินไม่พร้อมใช้งาน";
      if (/เช็คอินแล้ว|ครบทุกวันแล้ว/.test(errorMessage)) {
        await fetchCheckinActivity({ silent: true });
      }
      if (isMountedRef.current) {
        showToast(
          errorMessage,
          /เช็คอินแล้ว|ครบทุกวันแล้ว/.test(errorMessage) ? "warning" : "error",
        );
      }
    } finally {
      if (isMountedRef.current) {
        setCheckinSubmitting(false);
      }
    }
  };

  if (!shouldRenderCheckinWidget) {
    return null;
  }

  return (
    <>
      {/* ── Fixed Sidebar Trigger ── */}
      <div
        className="ck-trigger"
        style={{
          position: "fixed",
          top: "240px",
          right: 0,
          zIndex: 1000,
          background:
            "linear-gradient(160deg, rgba(20,20,20,0.96) 0%, rgba(12,12,12,0.93) 100%)",
          backdropFilter: "blur(20px) saturate(140%)",
          WebkitBackdropFilter: "blur(20px) saturate(140%)",
          padding: "12px 8px 14px",
          borderTopLeftRadius: "18px",
          borderBottomLeftRadius: "18px",
          width: "68px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "9px",
          userSelect: "none",
          boxShadow: `0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07), 0 0 24px ${checkinThemeColor}22`,
          transition:
            "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease",
          cursor: checkinSubmitting ? "not-allowed" : "pointer",
          borderRight: "none",
          borderLeft: `2px solid ${checkinThemeColor}55`,
        }}
        onClick={handleCheckinButtonClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateX(-5px)";
          e.currentTarget.style.boxShadow = `0 14px 44px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.12), 0 0 32px ${checkinThemeColor}33`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateX(0)";
          e.currentTarget.style.boxShadow = `0 8px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.07), 0 0 24px ${checkinThemeColor}22`;
        }}
      >
        {/* Ambient glow */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "-10px",
            width: "30px",
            height: "70px",
            background: `radial-gradient(ellipse, ${checkinThemeColor}55 0%, transparent 70%)`,
            transform: "translateY(-50%)",
            pointerEvents: "none",
            filter: "blur(10px)",
          }}
        />

        {/* Icon frame */}
        <div
          style={{
            position: "relative",
            width: "50px",
            height: "50px",
            borderRadius: "14px",
            background: `linear-gradient(145deg, ${checkinThemeColor}, ${checkinThemeColor}77)`,
            padding: "3px",
            boxShadow: `0 4px 18px ${checkinThemeColor}50`,
            transition: "transform 0.25s cubic-bezier(0.34,1.56,0.64,1)",
          }}
        >
          <img
            src={buildCheckinImageUrl(checkinActivity?.iconUrl)}
            alt="checkin-trigger"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "11px",
              display: "block",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "14px",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 55%)",
              pointerEvents: "none",
            }}
          />
        </div>

        {/* Live pulse dot */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "9px",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: checkinThemeColor,
            border: "2px solid rgba(12,12,12,0.9)",
            boxShadow: `0 0 8px ${checkinThemeColor}80`,
            animation: "ckPulse 2.2s ease-in-out infinite",
          }}
        />

        {(checkinActivity?.subtitle || checkinActivity?.title) && (
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "rgba(255,255,255,0.88)",
              textAlign: "center",
              lineHeight: 1.3,
              width: "100%",
              padding: "0 3px",
            }}
          >
            {checkinActivity?.subtitle || "เช็คอิน"}
          </span>
        )}
      </div>

      {/* ── Modal ── */}
      {checkinModalOpen && (
        <div className="ck-overlay" onClick={closeCheckinModal}>
          <div className="ck-modal" onClick={(e) => e.stopPropagation()}>
            {/* ── Close Button ── */}
            <button
              type="button"
              className="ck-close"
              onClick={closeCheckinModal}
              disabled={checkinSubmitting}
              aria-label="ปิด"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M1 1L11 11M1 11L11 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            {/* ── Hero Banner ── */}
            <div
              className="ck-hero"
              style={{
                background: `linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 18%, rgba(18,18,18,0.95)), rgba(18,18,18,0.98))`,
                borderColor: `color-mix(in srgb, var(--primaryColor) 20%, rgba(255,255,255,0.06))`,
              }}
            >
              <div
                className="ck-hero-glow"
                style={{ background: "var(--primaryColor)" }}
              />
              <div
                className="ck-hero-icon"
                style={{
                  background: `linear-gradient(145deg, var(--secondaryColor), color-mix(in srgb, var(--secondaryColor) 65%, #000))`,
                  boxShadow: `0 8px 32px color-mix(in srgb, var(--secondaryColor) 45%, transparent)`,
                }}
              >
                <img
                  src={buildCheckinImageUrl(checkinActivity?.iconUrl)}
                  alt="checkin"
                />
                <div className="ck-hero-icon-shine" />
              </div>
              <div
                className="ck-hero-badge"
                style={{
                  background: `color-mix(in srgb, var(--secondaryColor) 15%, rgba(0,0,0,0.4))`,
                  borderColor: `color-mix(in srgb, var(--secondaryColor) 40%, transparent)`,
                  color: `color-mix(in srgb, var(--secondaryColor) 80%, #fff)`,
                }}
              >
                <span
                  className="ck-hero-badge-dot"
                  style={{ background: "var(--secondaryColor)" }}
                />
                {checkinActivity?.subtitle || "กิจกรรมเช็คอิน"}
              </div>
              <h2 className="ck-hero-title">
                {checkinActivity?.title || "เช็คอินรับโชค"}
              </h2>
              <p className="ck-hero-desc">
                {checkinActivity?.description ||
                  "เช็คอินสะสมทุกวันเพื่อปลดล็อกรางวัลพิเศษทั้งเพชรและเงินสด"}
              </p>
            </div>

            {/* ── Progress + Stats Row ── */}
            <div className="ck-info-row">
              <div className="ck-progress-block">
                <div className="ck-progress-head">
                  <span className="ck-progress-label">ความคืบหน้า</span>
                  <span
                    className="ck-progress-value"
                    style={{ color: "var(--secondaryColor)" }}
                  >
                    {totalConfiguredDays > 0
                      ? Math.round(
                          (completedDaysDisplay / totalConfiguredDays) * 100,
                        )
                      : 0}
                    %
                  </span>
                </div>
                <div className="ck-progress-track">
                  <div
                    className="ck-progress-fill"
                    style={{
                      width: `${totalConfiguredDays > 0 ? (completedDaysDisplay / totalConfiguredDays) * 100 : 0}%`,
                      background: `linear-gradient(90deg, var(--secondaryColor), color-mix(in srgb, var(--secondaryColor) 80%, #fff))`,
                    }}
                  >
                    <div className="ck-progress-shine" />
                  </div>
                </div>
                <div className="ck-progress-meta">
                  <span>
                    {completedDaysDisplay}/{totalConfiguredDays} วัน
                  </span>
                  <span>
                    {progressState.streakCount > 0
                      ? `🔥 ต่อเนื่อง ${progressState.streakCount} วัน`
                      : ""}
                  </span>
                </div>
              </div>

              <div className="ck-stats-row">
                <div className="ck-stat-chip">
                  <span className="ck-stat-emoji">💎</span>
                  <div className="ck-stat-info">
                    <span className="ck-stat-num">
                      {totalCheckinRewards.diamond.toLocaleString()}
                    </span>
                    <span className="ck-stat-unit">เพชร</span>
                  </div>
                </div>
                <div className="ck-stat-chip">
                  <span className="ck-stat-emoji">💰</span>
                  <div className="ck-stat-info">
                    <span className="ck-stat-num">
                      {totalCheckinRewards.cash.toLocaleString()}
                    </span>
                    <span className="ck-stat-unit">บาท</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Error ── */}
            {!checkinLoading && checkinError && (
              <div className="ck-error">⚠️ {checkinError}</div>
            )}

            {/* ── Days Grid ── */}
            <div className="ck-days-section">
              <div className="ck-days-head">
                <h3 className="ck-days-label">รางวัลประจำวัน</h3>
                <span className="ck-days-line" />
              </div>
              <div className="ck-days-grid">
                {sortedCheckinRewards.map((reward, index) => {
                  const status = getDayStatus(reward.dayNumber);
                  const isSpecial =
                    reward.dayNumber === 7 || reward.rewardAmount >= 100;
                  return (
                    <div
                      key={`ck-day-${reward.dayNumber}`}
                      className={`ck-card ck-card--${status}${isSpecial ? " ck-card--special" : ""}`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      {/* Top strip */}
                      <div className="ck-card-top">
                        <span className="ck-card-day">
                          Day {reward.dayNumber}
                        </span>
                        {status === "current" && (
                          <span
                            className="ck-card-now"
                            style={{ background: checkinThemeColor }}
                          >
                            วันนี้
                          </span>
                        )}
                        {status === "completed" && (
                          <span className="ck-card-done">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                            >
                              <path
                                d="M2 5L4 7L8 3"
                                stroke="#22c55e"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </div>

                      {/* Icon */}
                      <div className="ck-card-icon">
                        {reward.rewardType === "cashWallet" ? "💸" : "💎"}
                      </div>

                      {/* Amount */}
                      <div className="ck-card-amount">
                        {describeReward(reward)}
                      </div>

                      {/* Date */}
                      <div className="ck-card-date">
                        {formatCheckinDate(reward.dayNumber) ||
                          `Day ${reward.dayNumber}`}
                      </div>

                      {/* Special glow */}
                      {isSpecial && (
                        <div
                          className="ck-card-glow"
                          style={{ background: checkinThemeColor }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CTA Button ── */}
            <div className="ck-action">
              <button
                type="button"
                className="ck-action-btn"
                onClick={handleStartCheckin}
                disabled={
                  checkinSubmitting || checkinLoading || !checkinActivity
                }
                style={{
                  background: `linear-gradient(135deg, var(--primaryColor) 0%, color-mix(in srgb, var(--primaryColor) 75%, #000) 100%)`,
                  boxShadow: `0 6px 24px color-mix(in srgb, var(--primaryColor) 40%, transparent)`,
                }}
              >
                <span className="ck-action-shine" />
                {checkinSubmitting ? (
                  <span className="ck-action-loading">
                    <span className="ck-spinner" />
                    กำลังเช็คอิน...
                  </span>
                ) : (
                  <span className="ck-action-content">
                    <svg
                      className="ck-action-ico"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <rect
                        x="3"
                        y="4"
                        width="18"
                        height="18"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M16 2V6M8 2V6M3 10H21"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M8 15L10.5 17.5L16 12.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>
                      {checkinActivity?.buttonText || "เช็คอินรับรางวัล"}
                    </span>
                    <svg
                      className="ck-action-arrow"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M3 7H11M8 4L11 7L8 10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
            </div>

            {/* ── Loading Overlay ── */}
            {checkinLoading && (
              <div className="ck-loading-overlay">
                <div
                  className="ck-loading-spin"
                  style={{ borderTopColor: checkinThemeColor }}
                />
                <span>กำลังโหลด...</span>
              </div>
            )}

            <style>{`
              /* ═══ OVERLAY ═══ */
              .ck-overlay {
                position: fixed; inset: 0;
                background: rgba(0,0,0,0.7);
                backdrop-filter: blur(10px) saturate(120%);
                -webkit-backdrop-filter: blur(10px) saturate(120%);
                z-index: 1500;
                display: flex; align-items: center; justify-content: center;
                padding: 16px;
                animation: ckFadeIn 0.2s ease;
              }

              /* ═══ MODAL ═══ */
              .ck-modal {
                width: 100%; max-width: 520px;
                max-height: 90vh; overflow-y: auto; overflow-x: hidden;
                background: rgba(18, 18, 18, 0.92);
                backdrop-filter: blur(24px) saturate(130%);
                -webkit-backdrop-filter: blur(24px) saturate(130%);
                border: 1px solid rgba(255,255,255,0.08);
                border-radius: 20px;
                padding: 0;
                position: relative; color: #fff;
                box-shadow:
                  0 32px 80px rgba(0,0,0,0.55),
                  0 0 0 1px rgba(255,255,255,0.04),
                  inset 0 1px 0 rgba(255,255,255,0.06);
                animation: ckSlideIn 0.35s cubic-bezier(0.16,1,0.3,1);
                font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans Thai", sans-serif;
              }
              .ck-modal::-webkit-scrollbar { width: 6px; }
              .ck-modal::-webkit-scrollbar-track { background: transparent; }
              .ck-modal::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
              .ck-modal::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

              /* ═══ CLOSE ═══ */
              .ck-close {
                position: absolute; top: 14px; right: 14px; z-index: 20;
                width: 36px; height: 36px;
                display: flex; align-items: center; justify-content: center;
                background: rgba(255,255,255,0.06);
                border: 1px solid rgba(255,255,255,0.08);
                border-radius: 10px;
                color: rgba(255,255,255,0.5);
                cursor: pointer;
                transition: all 0.2s ease;
              }
              .ck-close:hover {
                background: var(--primaryColor);
                color: #fff;
                border-color: var(--primaryColor);
                transform: rotate(90deg);
              }
              .ck-close:disabled { opacity: 0.35; cursor: not-allowed; }

              /* ═══ HERO BANNER ═══ */
              .ck-hero {
                position: relative;
                padding: 36px 24px 28px;
                text-align: center;
                border-bottom: 1px solid;
                overflow: hidden;
                border-radius: 20px 20px 0 0;
              }
              .ck-hero-glow {
                position: absolute;
                top: -60px; left: 50%; transform: translateX(-50%);
                width: 260px; height: 260px;
                border-radius: 50%;
                filter: blur(90px);
                opacity: 0.12;
                pointer-events: none;
              }
              .ck-hero-icon {
                width: 72px; height: 72px;
                border-radius: 20px;
                margin: 0 auto 16px;
                padding: 3px;
                position: relative;
                overflow: hidden;
                animation: ckFloat 3s ease-in-out infinite;
              }
              .ck-hero-icon img {
                width: 100%; height: 100%;
                object-fit: cover;
                border-radius: 17px;
                display: block;
                position: relative; z-index: 1;
              }
              .ck-hero-icon-shine {
                position: absolute; inset: 0;
                border-radius: 20px;
                background: linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 50%);
                pointer-events: none; z-index: 2;
              }
              .ck-hero-badge {
                display: inline-flex; align-items: center; gap: 6px;
                padding: 4px 12px;
                border: 1px solid;
                border-radius: 999px;
                font-size: 0.72rem; font-weight: 600;
                margin-bottom: 10px;
              }
              .ck-hero-badge-dot {
                width: 6px; height: 6px; border-radius: 50%;
                animation: ckPulse 2s ease-in-out infinite;
              }
              .ck-hero-title {
                margin: 0 0 6px;
                font-size: 1.4rem; font-weight: 800;
                color: #fff;
                line-height: 1.25;
              }
              .ck-hero-desc {
                margin: 0;
                font-size: 0.82rem;
                color: rgba(255,255,255,0.45);
                line-height: 1.5;
                max-width: 360px;
                margin-inline: auto;
              }

              /* ═══ INFO ROW ═══ */
              .ck-info-row {
                padding: 18px 22px 0;
              }

              /* ═══ PROGRESS ═══ */
              .ck-progress-block {
                margin-bottom: 14px;
              }
              .ck-progress-head {
                display: flex; align-items: center; justify-content: space-between;
                margin-bottom: 8px;
              }
              .ck-progress-label {
                font-size: 0.72rem; font-weight: 700;
                color: rgba(255,255,255,0.45);
                text-transform: uppercase; letter-spacing: 0.06em;
              }
              .ck-progress-value {
                font-size: 0.88rem; font-weight: 800;
              }
              .ck-progress-track {
                height: 6px;
                background: rgba(255,255,255,0.06);
                border-radius: 8px;
                overflow: hidden;
              }
              .ck-progress-fill {
                height: 100%; border-radius: 8px;
                position: relative; overflow: hidden;
                transition: width 0.7s cubic-bezier(0.16,1,0.3,1);
                min-width: 4px;
              }
              .ck-progress-shine {
                position: absolute; top: 0; left: 0;
                width: 60%; height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
                animation: ckShimmer 2.5s infinite;
              }
              .ck-progress-meta {
                display: flex; justify-content: space-between;
                margin-top: 6px;
                font-size: 0.7rem; color: rgba(255,255,255,0.35);
              }

              /* ═══ STATS ROW ═══ */
              .ck-stats-row {
                display: grid; grid-template-columns: 1fr 1fr;
                gap: 10px;
                margin-bottom: 16px;
              }
              .ck-stat-chip {
                display: flex; align-items: center; gap: 10px;
                background: rgba(255,255,255,0.04);
                border: 1px solid rgba(255,255,255,0.07);
                border-radius: 14px;
                padding: 12px 14px;
                transition: all 0.2s ease;
              }
              .ck-stat-chip:hover {
                background: rgba(255,255,255,0.07);
                border-color: rgba(255,255,255,0.14);
                transform: translateY(-2px);
                box-shadow: 0 4px 16px rgba(0,0,0,0.3);
              }
              .ck-stat-emoji { font-size: 1.5rem; flex-shrink: 0; }
              .ck-stat-info { display: flex; flex-direction: column; }
              .ck-stat-num { font-size: 1.05rem; font-weight: 800; color: #fff; line-height: 1.2; }
              .ck-stat-unit { font-size: 0.65rem; font-weight: 600; color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 0.04em; }

              /* ═══ ERROR ═══ */
              .ck-error {
                margin: 0 22px 14px;
                display: flex; align-items: center; gap: 8px;
                background: color-mix(in srgb, #ef4444 16%, rgba(18,18,18,0.9));
                border: 1px solid color-mix(in srgb, #ef4444 36%, transparent);
                border-radius: 12px; padding: 11px 14px;
                color: #fca5a5; font-size: 0.82rem;
              }

              /* ═══ DAYS SECTION ═══ */
              .ck-days-section {
                padding: 0 22px;
              }
              .ck-days-head {
                display: flex; align-items: center; gap: 10px;
                margin-bottom: 12px;
              }
              .ck-days-label {
                margin: 0;
                font-size: 0.7rem; font-weight: 800;
                color: rgba(255,255,255,0.45);
                text-transform: uppercase; letter-spacing: 0.08em;
                white-space: nowrap;
              }
              .ck-days-line {
                flex: 1; height: 1px;
                background: linear-gradient(90deg, rgba(255,255,255,0.1), transparent);
              }
              .ck-days-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
                gap: 8px;
              }

              /* ═══ DAY CARD ═══ */
              .ck-card {
                position: relative; text-align: center;
                background: rgba(255,255,255,0.03);
                border: 1px solid rgba(255,255,255,0.07);
                border-radius: 14px;
                padding: 12px 6px 10px;
                overflow: hidden;
                animation: ckCardIn 0.35s ease both;
                transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
              }
              .ck-card:hover {
                transform: translateY(-3px);
                background: rgba(255,255,255,0.06);
                box-shadow: 0 6px 20px rgba(0,0,0,0.3);
              }

              /* States */
              .ck-card--completed {
                background: color-mix(in srgb, #22c55e 8%, rgba(18,18,18,0.9));
                border-color: color-mix(in srgb, #22c55e 28%, transparent);
              }
              .ck-card--current {
                background: color-mix(in srgb, var(--primaryColor) 12%, rgba(18,18,18,0.95));
                border-color: color-mix(in srgb, var(--primaryColor) 55%, transparent);
                box-shadow: 0 0 24px color-mix(in srgb, var(--primaryColor) 20%, transparent);
              }
              .ck-card--upcoming { opacity: 0.5; }
              .ck-card--special {
                background: linear-gradient(145deg,
                  color-mix(in srgb, var(--primaryColor) 12%, rgba(18,18,18,0.92)),
                  rgba(18,18,18,0.95));
                border-color: color-mix(in srgb, var(--primaryColor) 35%, transparent);
              }

              /* Card inner */
              .ck-card-top {
                display: flex; align-items: center; justify-content: space-between;
                margin-bottom: 6px; min-height: 18px;
              }
              .ck-card-day {
                font-size: 0.58rem; font-weight: 700;
                color: rgba(255,255,255,0.25);
                text-transform: uppercase; letter-spacing: 0.04em;
              }
              .ck-card-now {
                font-size: 0.52rem; font-weight: 800;
                color: #fff; padding: 2px 6px;
                border-radius: 6px;
                text-transform: uppercase; letter-spacing: 0.03em;
              }
              .ck-card-done {
                width: 18px; height: 18px; border-radius: 50%;
                background: color-mix(in srgb, #22c55e 20%, rgba(18,18,18,0.9));
                border: 1px solid color-mix(in srgb, #22c55e 40%, transparent);
                display: flex; align-items: center; justify-content: center;
              }
              .ck-card-icon { font-size: 1.55rem; margin-bottom: 4px; }
              .ck-card-amount { font-size: 0.78rem; font-weight: 700; color: #fff; margin-bottom: 3px; }
              .ck-card-date { font-size: 0.55rem; color: rgba(255,255,255,0.2); }
              .ck-card-glow {
                position: absolute; bottom: -20px; left: 50%;
                transform: translateX(-50%);
                width: 50px; height: 50px; border-radius: 50%;
                filter: blur(20px); opacity: 0.18;
                pointer-events: none;
              }

              /* ═══ CTA ═══ */
              .ck-action {
                padding: 20px 22px 22px;
                position: relative;
              }
              .ck-action-btn {
                width: 100%;
                padding: 15px 20px;
                border: none; border-radius: 14px;
                color: #fff; cursor: pointer;
                font-size: 0.95rem; font-weight: 700;
                position: relative; overflow: hidden;
                transition: transform 0.2s ease, box-shadow 0.3s ease, opacity 0.2s ease;
              }
              .ck-action-btn:hover:not(:disabled) {
                transform: translateY(-2px);
                filter: brightness(1.08);
              }
              .ck-action-btn:active:not(:disabled) { transform: translateY(0); }
              .ck-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
              .ck-action-shine {
                position: absolute; top: 0; left: -100%;
                width: 60%; height: 100%;
                background: linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%);
                pointer-events: none;
                animation: ckShineSlide 3s ease-in-out infinite;
              }
              .ck-action-content {
                display: flex; align-items: center; justify-content: center; gap: 10px;
                position: relative; z-index: 1;
              }
              .ck-action-ico { flex-shrink: 0; }
              .ck-action-arrow {
                flex-shrink: 0; opacity: 0.7;
                transition: transform 0.2s ease;
              }
              .ck-action-btn:hover:not(:disabled) .ck-action-arrow { transform: translateX(4px); }
              .ck-action-loading {
                display: flex; align-items: center; justify-content: center; gap: 10px;
                position: relative; z-index: 1;
                font-size: 0.95rem; font-weight: 700;
              }

              /* ═══ LOADING OVERLAY ═══ */
              .ck-loading-overlay {
                position: absolute; inset: 0;
                background: rgba(0,0,0,0.65);
                backdrop-filter: blur(6px);
                display: flex; flex-direction: column;
                align-items: center; justify-content: center; gap: 14px;
                border-radius: 20px; z-index: 50;
                color: rgba(255,255,255,0.6); font-size: 0.85rem;
              }
              .ck-loading-spin {
                width: 36px; height: 36px;
                border: 3px solid rgba(255,255,255,0.1);
                border-radius: 50%;
                animation: ckSpin 1s linear infinite;
              }

              /* ═══ SPINNER ═══ */
              .ck-spinner {
                display: inline-block; width: 16px; height: 16px; flex-shrink: 0;
                border: 2px solid rgba(255,255,255,0.28);
                border-top-color: #fff; border-radius: 50%;
                animation: ckSpin 0.8s linear infinite;
              }

              /* ═══ KEYFRAMES ═══ */
              @keyframes ckFadeIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes ckSlideIn {
                from { opacity: 0; transform: translateY(16px) scale(0.97); }
                to   { opacity: 1; transform: translateY(0) scale(1); }
              }
              @keyframes ckCardIn {
                from { opacity: 0; transform: translateY(8px); }
                to   { opacity: 1; transform: translateY(0); }
              }
              @keyframes ckShimmer {
                0%   { transform: translateX(-100%); }
                100% { transform: translateX(220%); }
              }
              @keyframes ckShineSlide {
                0%        { left: -100%; }
                55%, 100% { left: 150%; }
              }
              @keyframes ckFloat {
                0%, 100% { transform: translateY(0); }
                50%       { transform: translateY(-6px); }
              }
              @keyframes ckSpin { to { transform: rotate(360deg); } }
              @keyframes ckPulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50%       { transform: scale(1.25); opacity: 0.7; }
              }

              /* ═══ RESPONSIVE ═══ */
              @media (max-width: 600px) {
                .ck-modal { max-width: 100%; border-radius: 18px; }
                .ck-hero { padding: 30px 18px 22px; border-radius: 18px 18px 0 0; }
                .ck-hero-icon { width: 60px; height: 60px; border-radius: 16px; }
                .ck-hero-icon img { border-radius: 13px; }
                .ck-hero-icon-shine { border-radius: 16px; }
                .ck-hero-title { font-size: 1.2rem; }
                .ck-info-row { padding: 14px 16px 0; }
                .ck-stats-row { gap: 8px; }
                .ck-stat-chip { padding: 10px 12px; }
                .ck-days-section { padding: 0 16px; }
                .ck-days-grid { grid-template-columns: repeat(3, 1fr); gap: 7px; }
                .ck-card { padding: 10px 5px 8px; }
                .ck-card-icon { font-size: 1.3rem; }
                .ck-card-amount { font-size: 0.72rem; }
                .ck-action { padding: 16px 16px 18px; }
                .ck-close { top: 10px; right: 10px; width: 32px; height: 32px; }
              }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckinExperience;
